// Third-party
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import deepmerge from 'deepmerge';
import Highcharts, { SeriesScatterOptions, Point } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

// Components
import Crosshair from './Crosshair';

// Utils
import dom from '../../../../utils/dom';
import { formatNumber } from '../../../../utils/i18n';
import Nullable from '@/types/nullable';

// Creating type aliases to centralize Highcharts dependency in ScatterPlot.tsx
export type ScatterPlotDatum = SeriesScatterOptions;
export type ScatterPlotOptions = Highcharts.Options;

type Props = {
  dark?: boolean;
  data: ScatterPlotDatum[];
  height: Nullable<number>;
  chartOptions?: Partial<ScatterPlotOptions>;
};

type State = {
  chartOptions: ScatterPlotOptions;
  crosshairKeys: string[];
  svgCrosshairs: React.ReactElement[];
};

class ScatterPlot extends Component<Props, State> {
  chartRef = React.createRef<HighchartsReact>();

  static defaultProps = {
    height: null,
  };

  readonly DEFAULT_CHART_OPTIONS: ScatterPlotOptions = {
    series: [],
    tooltip: {
      useHTML: true,
      backgroundColor: '#000000',
      borderColor: '#000000',
      borderRadius: 6,
      shadow: false,
      valueDecimals: 2,
      style: {
        color: '#ffffff',
      },
    },
    xAxis: {
      labels: {
        formatter() {
          return formatNumber(this.value, 1);
        },
      },
    },
    yAxis: {
      title: {
        text: undefined,
      },
      labels: {
        formatter() {
          return formatNumber(this.value, 1);
        },
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              radiusPlus: 0,
            },
          },
        },
      },
      series: {
        animation: false,
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    navigator: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    chart: {
      type: 'scatter',
      animation: false,
    },
    title: {
      text: undefined,
    },
  };

  readonly state: State = {
    chartOptions: deepmerge(this.DEFAULT_CHART_OPTIONS, this.props.chartOptions || {}),
    svgCrosshairs: [],
    crosshairKeys: [],
  };

  componentDidMount() {
    this.setSeries();

    window.addEventListener('resize', this.onResize);

    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.chartOptions !== prevProps.chartOptions) {
      this.setChartOptions();
    }

    if (this.props.data !== prevProps.data) {
      this.setSeries();
    }

    this.reorderGraphSvgLayers();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onResize = debounce(() => this.repositionCrosshairs(), 100);

  repositionCrosshairs = () => {
    setTimeout(() => {
      this.focusDataPoints(this.state.crosshairKeys);
    }, 0);
  };

  reorderGraphSvgLayers = () => {
    /**
     * The order of the layers somehow gets mixed up while adding the crosshairs. Luckily,
     * Highcharts uses a 'data-z-index' property to sort the layers, so we'll simpy make
     * sure that the index is respected on every update of the chart.
     */
    if (!this.chartRef.current || !this.chartRef.current.container.current) {
      return;
    }

    const container = this.chartRef.current.container.current.getElementsByClassName('highcharts-root')[0];
    if (!container) {
      return;
    }

    const getZIndex = (element: HTMLElement) => {
      const zIndexAttr = element.dataset && element.dataset.zIndex;
      return zIndexAttr ? parseInt(zIndexAttr, 10) : 0;
    };

    const children = [].slice.call(container.children);
    children.sort((a: HTMLElement, b: HTMLElement) => getZIndex(a) - getZIndex(b));

    container.childNodes.forEach(node => node.remove());

    for (const child of children) {
      container.appendChild(child);
    }
  };

  getCrosshairsForPoints = (points: Point[]) => {
    if (!this.chartRef.current) {
      return [];
    }

    const container = this.chartRef.current.container.current;
    if (!container) {
      return [];
    }

    const chartBox = container.getElementsByClassName('highcharts-plot-background')[0] as SVGRectElement;
    const containerBBox = chartBox.getBBox();

    const crosshairStyles = {
      transform: `translate(${containerBBox.x}px, ${containerBBox.y}px)`,
    };

    return points
      .map(p => {
        if (!p.graphic) {
          return null;
        }

        const { width, height, x, y } = p.graphic.element.getBBox();
        const center = {
          x: x + width / 2,
          y: y + height / 2,
        };

        return <Crosshair style={crosshairStyles} key={p.key} center={center} container={containerBBox} color={p.color.toString()} />;
      })
      .filter(element => !!element) as JSX.Element[];
  };

  setSeries = () => {
    this.setState(prevState => ({
      chartOptions: {
        ...prevState.chartOptions,
        series: this.props.data,
      },
    }));
  };

  setChartOptions = () => {
    this.setState(prevState => ({
      chartOptions: deepmerge(prevState.chartOptions, this.props.chartOptions || {}),
    }));
  };

  injectCrosshairs = () => {
    const { svgCrosshairs } = this.state;

    if (!svgCrosshairs.length || !this.chartRef.current || !this.chartRef.current.container.current) {
      return null;
    }

    /**
     * The only way to ensure that the crosshairs do not render over the tooltips is to
     * inject them directly into the Highcharts container and to adjust the z-index properties afterwards.
     */
    const container = this.chartRef.current.container.current.getElementsByClassName('highcharts-root')[0];
    if (!container) {
      return null;
    }

    return ReactDOM.createPortal(svgCrosshairs, container);
  };

  unfocusDataPoints(keys: string[]) {
    for (const key of keys) {
      this.findDataPoints(key).forEach(p => p.setState(''));
    }
  }

  focusDataPoints(crosshairKeys: string[]) {
    const points = crosshairKeys.reduce((acc, key) => acc.concat(this.findDataPoints(key)), [] as Point[]);

    const svgCrosshairs = this.getCrosshairsForPoints(points);

    this.setState({ svgCrosshairs, crosshairKeys });
  }

  findDataPoints(key: string) {
    if (!this.chartRef.current) {
      return [];
    }

    return this.chartRef.current.chart.series.reduce((acc, serie) => acc.concat(serie.points.filter(p => p.key === key)), [] as Point[]);
  }

  render() {
    const { dark } = this.props;
    const { chartOptions } = this.state;

    return (
      <div className={classNames('scatter-plot', { dark })}>
        {this.injectCrosshairs()}

        <HighchartsReact ref={this.chartRef} containerProps={{ className: 'highcharts-react-container' }} highcharts={Highcharts} options={chartOptions} />
      </div>
    );
  }
}

export default ScatterPlot;
